import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { fmtUSD, fmtNum, shortDate } from "@/lib/format";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip as RTooltip, CartesianGrid, ResponsiveContainer,
  RadialBarChart, RadialBar,
} from "recharts";
import {
  Utensils, Users, Warehouse, Truck, Banknote, Leaf, AlertTriangle,
  CheckCircle2, Clock, Target, Soup, Flame, Building2, Gauge,
  Activity, Download, Bell, MapPin, TrendingUp, TrendingDown,
  XCircle,
} from "@/components/icons/console-icons";

const FOREST = "#0d6b3d";
const GOLD = "#d4a017";
const TERRA = "#c96a4a";
const LEAF = "#5c9a62";
const SLATE = "#667085";
const ALERT = "#d64545";
const SUCCESS = "#2e8b57";
const INFO = "#3b82f6";

type MealPlan = {
  id: string;
  school_id: string;
  meal_date: string;
  meal_type: string;
  planned_portions: number;
  prepared_portions: number;
  delivered_portions: number;
  consumed_portions: number;
  wasted_portions: number;
  cost_per_portion: number;
  status: string;
  school?: { name: string };
  recipe?: { name: string };
  kitchen?: { name: string };
};

type School = {
  id: string;
  name: string;
  district: string;
  student_count: number;
  latitude: number | null;
  longitude: number | null;
};

type Kitchen = {
  id: string;
  name: string;
  location: string;
  capacity_per_day: number;
  current_load: number;
  status: string;
  manager_name: string;
};

type Delivery = {
  id: string;
  school_id: string;
  portions: number;
  status: string;
  dispatched_at: string;
  delivered_at: string | null;
  school?: { name: string };
};

type Ingredient = {
  id: string;
  name: string;
  unit: string;
  category: string;
  current_stock: number;
  reorder_level: number;
  cost_per_unit: number;
  storage_location: string;
};

type Student = {
  id: string;
  full_name: string;
  school_id: string;
  grade: string;
  class_name: string;
  enrollment_status: string;
  feeding_eligible: boolean;
  gender: string;
  school?: { name: string; district: string };
};

type Vehicle = {
  id: string;
  registration: string;
  vehicle_type: string;
  capacity: number;
  status: string;
  school_id: string | null;
};

function KPI({ icon: Icon, label, value, sub, tint }: { icon: any; label: string; value: string; sub?: string; tint: string }) {
  return (
    <div className="rounded-xl border bg-card p-3.5" style={{ borderColor: `${tint}20` }}>
      <div className="flex items-center gap-2.5">
        <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg" style={{ background: `${tint}15`, color: tint }}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground truncate">{label}</div>
          <div className="font-mono text-lg font-bold" style={{ color: tint }}>{value}</div>
          {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const cfg: Record<string, { label: string; color: string; bg: string }> = {
    completed: { label: "Completed", color: "text-emerald-700", bg: "bg-emerald-50" },
    in_progress: { label: "In Progress", color: "text-blue-700", bg: "bg-blue-50" },
    planned: { label: "Planned", color: "text-slate-600", bg: "bg-slate-100" },
    delayed: { label: "Delayed", color: "text-red-700", bg: "bg-red-50" },
    delivered: { label: "Delivered", color: "text-emerald-700", bg: "bg-emerald-50" },
    in_transit: { label: "In Transit", color: "text-amber-700", bg: "bg-amber-50" },
    pending: { label: "Pending", color: "text-slate-600", bg: "bg-slate-100" },
    confirmed: { label: "Confirmed", color: "text-emerald-700", bg: "bg-emerald-50" },
  };
  const c = cfg[status] ?? { label: status, color: "text-slate-600", bg: "bg-slate-100" };
  return <Badge variant="outline" className={`text-[10px] ${c.color} ${c.bg} border-current/20`}>{c.label}</Badge>;
}

export function FeedingCommandCenter() {
  const [activeTab, setActiveTab] = useState("overview");

  const { data: schools = [], isLoading: schoolsLoading } = useQuery({
    queryKey: ["zw-schools-feeding"],
    queryFn: async () => (await supabase.from("schools").select("id, name, district, student_count, latitude, longitude").order("name").limit(30)).data ?? [] as School[],
  });

  const { data: mealPlans = [], isLoading: plansLoading } = useQuery({
    queryKey: ["zw-meal-plans-today"],
    queryFn: async () => {
      const today = new Date().toISOString().slice(0, 10);
      const { data } = await supabase
        .from("zw_meal_plans")
        .select("*, school:schools(name), recipe:zw_meal_recipes(name), kitchen:zw_kitchens(name)")
        .eq("meal_date", today)
        .limit(30);
      return (data ?? []) as MealPlan[];
    },
  });

  const { data: kitchens = [] } = useQuery({
    queryKey: ["zw-kitchens"],
    queryFn: async () => (await supabase.from("zw_kitchens").select("*").limit(10)).data ?? [] as Kitchen[],
  });

  const { data: deliveries = [] } = useQuery({
    queryKey: ["zw-deliveries-today"],
    queryFn: async () => {
      const today = new Date().toISOString().slice(0, 10);
      const { data } = await supabase
        .from("zw_meal_deliveries")
        .select("*, school:schools(name)")
        .eq("delivery_date", today)
        .limit(30);
      return (data ?? []) as Delivery[];
    },
  });

  const { data: ingredients = [] } = useQuery({
    queryKey: ["zw-ingredients"],
    queryFn: async () => (await supabase.from("zw_ingredients").select("*").order("name").limit(30)).data ?? [] as Ingredient[],
  });

  const { data: students = [] } = useQuery({
    queryKey: ["zw-students-feeding"],
    queryFn: async () => (await supabase.from("zw_students").select("id, full_name, school_id, grade, class_name, enrollment_status, feeding_eligible, gender, school:schools(name, district)").eq("enrollment_status", "enrolled").limit(50).order("full_name")).data ?? [] as Student[],
  });

  const { data: vehicles = [] } = useQuery({
    queryKey: ["zw-vehicles-feeding"],
    queryFn: async () => (await supabase.from("zw_vehicles").select("*").limit(10)).data ?? [] as Vehicle[],
  });

  const { data: studentCount } = useQuery({
    queryKey: ["zw-student-count"],
    queryFn: async () => (await supabase.from("zw_students").select("*", { count: "exact", head: true })).count ?? 0,
  });

  const { data: attendanceData = [] } = useQuery({
    queryKey: ["zw-attendance-today"],
    queryFn: async () => {
      const today = new Date().toISOString().slice(0, 10);
      const { data } = await supabase.from("zw_attendance").select("status").eq("attendance_date", today).limit(2000);
      return data ?? [];
    },
  });

  // Computed KPIs
  const totalStudents = studentCount ?? 0;
  const totalSchools = schools.length;
  const totalPlanned = mealPlans.reduce((a, p) => a + (p.planned_portions ?? 0), 0);
  const totalPrepared = mealPlans.reduce((a, p) => a + (p.prepared_portions ?? 0), 0);
  const totalDelivered = mealPlans.reduce((a, p) => a + (p.delivered_portions ?? 0), 0);
  const totalConsumed = mealPlans.reduce((a, p) => a + (p.consumed_portions ?? 0), 0);
  const totalWasted = mealPlans.reduce((a, p) => a + (p.wasted_portions ?? 0), 0);
  const totalRemaining = totalPrepared - totalConsumed - totalWasted;
  const dailyCost = mealPlans.reduce((a, p) => a + (p.consumed_portions * (p.cost_per_portion ?? 0)), 0);
  const avgCostPerMeal = totalConsumed > 0 ? dailyCost / totalConsumed : 0;
  const costPerStudent = totalStudents > 0 ? dailyCost / totalStudents : 0;
  const presentCount = attendanceData.filter((a: any) => a.status === "present").length;
  const absentCount = attendanceData.filter((a: any) => a.status === "absent").length;
  const lateCount = attendanceData.filter((a: any) => a.status === "late").length;
  const attendanceRate = attendanceData.length > 0 ? Math.round((presentCount / attendanceData.length) * 100) : 0;

  const delayedSchools = mealPlans.filter(p => p.status === "delayed").length;
  const lowStockIngredients = ingredients.filter(i => i.current_stock <= i.reorder_level);

  // Chart data
  const mealFlowData = useMemo(() => {
    const stages = [
      { name: "Planned", value: totalPlanned, fill: FOREST },
      { name: "Prepared", value: totalPrepared, fill: LEAF },
      { name: "Delivered", value: totalDelivered, fill: GOLD },
      { name: "Consumed", value: totalConsumed, fill: SUCCESS },
      { name: "Wasted", value: totalWasted, fill: ALERT },
    ];
    return stages;
  }, [totalPlanned, totalPrepared, totalDelivered, totalConsumed, totalWasted]);

  const schoolMealData = useMemo(() =>
    mealPlans.map(p => ({
      name: p.school?.name?.slice(0, 15) ?? "—",
      Planned: p.planned_portions ?? 0,
      Consumed: p.consumed_portions ?? 0,
    })).slice(0, 12),
  [mealPlans]);

  const kitchenLoadData = useMemo(() =>
    kitchens.map(k => ({
      name: k.name?.slice(0, 15) ?? "—",
      Load: k.current_load ?? 0,
      Capacity: k.capacity_per_day ?? 0,
    })),
  [kitchens]);

  const stockData = useMemo(() =>
    ingredients.slice(0, 10).map(i => ({
      name: i.name?.slice(0, 12) ?? "—",
      Stock: i.current_stock ?? 0,
      Reorder: i.reorder_level ?? 0,
    })),
  [ingredients]);

  const deliveryStatusData = useMemo(() => {
    const statuses: Record<string, number> = {};
    deliveries.forEach(d => { statuses[d.status] = (statuses[d.status] ?? 0) + 1; });
    return Object.entries(statuses).map(([name, value]) => ({ name, value }));
  }, [deliveries]);

  const isLoading = schoolsLoading || plansLoading;

  // Exceptions
  const exceptions = useMemo(() => {
    const ex: { sev: string; text: string; tint: string }[] = [];
    if (delayedSchools > 0) ex.push({ sev: "critical", text: `${delayedSchools} school(s) have delayed meal delivery`, tint: ALERT });
    if (lowStockIngredients.length > 0) ex.push({ sev: "high", text: `${lowStockIngredients.length} ingredients below reorder level`, tint: GOLD });
    if (absentCount > totalStudents * 0.15) ex.push({ sev: "medium", text: `Attendance drop: ${absentCount} students absent today`, tint: TERRA });
    if (totalWasted > totalConsumed * 0.05) ex.push({ sev: "medium", text: `Wastage above 5% threshold`, tint: TERRA });
    return ex;
  }, [delayedSchools, lowStockIngredients, absentCount, totalStudents, totalWasted, totalConsumed]);

  const FS = "'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif";

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-pulse text-muted-foreground">Loading feeding operations...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950">
      {/* Hero Header */}
      <div className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${FOREST} 0%, #0a4a30 50%, #062818 100%)` }}>
        <div className="pointer-events-none absolute -right-24 top-0 h-72 w-72 rounded-full" style={{ background: `radial-gradient(closest-side, rgba(212,160,23,.14), transparent 70%)` }} />
        <div className="pointer-events-none absolute -left-16 bottom-0 h-48 w-48 rounded-full" style={{ background: `radial-gradient(closest-side, rgba(201,106,74,.10), transparent 70%)` }} />
        <div className="relative mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-10">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em]" style={{ color: GOLD }}>
                <span className="h-px w-6" style={{ background: TERRA }} /> BRECCS Zimbabwe · School Feeding Command Center
              </div>
              <h1 className="mt-3 text-2xl font-bold text-white md:text-3xl" style={{ fontFamily: FS }}>
                National School Feeding Operations
              </h1>
              <p className="mt-1 text-sm text-white/70">
                Real-time feeding intelligence across {fmtNum(totalSchools)} schools, {fmtNum(totalStudents)} enrolled students. All data live from the database.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="secondary" className="border-white/15 bg-white/10 text-white hover:bg-white/20" onClick={() => window.print()}>
                <Download className="mr-1 h-4 w-4" /> Export
              </Button>
            </div>
          </div>

          {/* Operational Status Banner */}
          <div className="mt-5 flex flex-wrap items-center gap-3 rounded-xl border px-4 py-2.5 backdrop-blur-sm"
            style={{ borderColor: exceptions.length === 0 ? "rgba(46,139,87,.25)" : "rgba(214,69,69,.25)", background: exceptions.length === 0 ? "rgba(46,139,87,.08)" : "rgba(214,69,69,.08)" }}>
            <div className="grid h-8 w-8 place-items-center rounded-full" style={{ background: exceptions.length === 0 ? "rgba(46,139,87,.2)" : "rgba(214,69,69,.2)" }}>
              {exceptions.length === 0
                ? <CheckCircle2 className="h-4 w-4 text-emerald-300" />
                : <AlertTriangle className="h-4 w-4 text-red-300" />}
            </div>
            <div className="flex-1">
              <span className="text-sm font-medium" style={{ color: exceptions.length === 0 ? "#6ee7b7" : "#fca5a5" }}>
                {exceptions.length === 0 ? "ALL SYSTEMS OPERATIONAL" : `${exceptions.length} ACTION(S) REQUIRED`}
              </span>
              <span className="ml-2 text-xs text-white/60">
                {exceptions.length === 0
                  ? `Feeding on track. ${fmtNum(totalConsumed)} meals consumed today.`
                  : `${delayedSchools} delayed, ${lowStockIngredients.length} low stock alerts`}
              </span>
            </div>
            <Badge className="border-emerald-400/30 bg-emerald-500/20 text-emerald-200">Live Data</Badge>
          </div>

          {/* KPI Grid */}
          <div className="mt-5 grid grid-cols-2 gap-2 md:grid-cols-4 lg:grid-cols-8">
            <KPI icon={Users} label="Enrolled" value={fmtNum(totalStudents)} tint={FOREST} />
            <KPI icon={Target} label="Meals Planned" value={fmtNum(totalPlanned)} tint={LEAF} />
            <KPI icon={Utensils} label="Prepared" value={fmtNum(totalPrepared)} tint={GOLD} />
            <KPI icon={Truck} label="Delivered" value={fmtNum(totalDelivered)} tint={INFO} />
            <KPI icon={Soup} label="Consumed" value={fmtNum(totalConsumed)} tint={SUCCESS} />
            <KPI icon={Building2} label="Schools" value={fmtNum(totalSchools)} tint={FOREST} />
            <KPI icon={Banknote} label="Cost/Student" value={fmtUSD(costPerStudent)} tint={GOLD} />
            <KPI icon={Gauge} label="Wastage" value={`${totalWasted}`} tint={ALERT} />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="mx-auto max-w-7xl px-4 py-6 md:px-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4 flex flex-wrap">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="schools">Schools</TabsTrigger>
            <TabsTrigger value="kitchens">Kitchens</TabsTrigger>
            <TabsTrigger value="inventory">Inventory</TabsTrigger>
            <TabsTrigger value="deliveries">Deliveries</TabsTrigger>
            <TabsTrigger value="transport">Transport</TabsTrigger>
          </TabsList>

          {/* OVERVIEW */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid gap-4 lg:grid-cols-[1.3fr_1fr]">
              {/* Meal Flow Chart */}
              <Card className="p-5">
                <div className="mb-4 flex items-start justify-between">
                  <div>
                    <div className="text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground">Pipeline</div>
                    <h3 className="mt-0.5 text-base font-semibold">Meal Production Pipeline</h3>
                  </div>
                  <Badge variant="outline" className="text-[10px]">Today</Badge>
                </div>
                <div className="h-64">
                  <ResponsiveContainer>
                    <BarChart data={mealFlowData}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <RTooltip />
                      <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                        {mealFlowData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              {/* Exceptions */}
              <Card className="p-5">
                <div className="mb-4 flex items-start justify-between">
                  <div>
                    <div className="text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground">Action Centre</div>
                    <h3 className="mt-0.5 text-base font-semibold">Exceptions & Alerts</h3>
                  </div>
                  <Badge variant="outline" className="text-[10px]"><Bell className="mr-1 h-3 w-3" />{exceptions.length}</Badge>
                </div>
                {exceptions.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <CheckCircle2 className="h-10 w-10 text-emerald-500 mb-2" />
                    <p className="text-sm font-medium text-muted-foreground">All systems operational</p>
                    <p className="text-xs text-muted-foreground/60 mt-1">No exceptions detected</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {exceptions.map((e, i) => (
                      <div key={i} className="rounded-lg border p-3" style={{ borderColor: `${e.tint}40`, background: `${e.tint}08` }}>
                        <div className="flex items-start gap-3">
                          <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full" style={{ background: `${e.tint}18`, color: e.tint }}>
                            {e.sev === "critical" ? <AlertTriangle className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
                          </div>
                          <div className="flex-1">
                            <Badge variant="outline" className="mb-1 text-[9px] uppercase" style={{ borderColor: `${e.tint}40`, color: e.tint }}>{e.sev}</Badge>
                            <div className="text-sm">{e.text}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>

            {/* Attendance + Cost */}
            <div className="grid gap-4 lg:grid-cols-2">
              <Card className="p-5">
                <div className="mb-4">
                  <div className="text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground">Attendance</div>
                  <h3 className="mt-0.5 text-base font-semibold">Today's Attendance Overview</h3>
                </div>
                <div className="flex items-center gap-6">
                  <div className="relative grid h-36 w-36 shrink-0 place-items-center">
                    <RadialBarChart width={144} height={144} data={[{ name: "Rate", value: attendanceRate, fill: FOREST }]} innerRadius="60%" outerRadius="100%">
                      <RadialBar dataKey="value" cornerRadius={6} background />
                    </RadialBarChart>
                    <div className="absolute text-xl font-bold" style={{ color: FOREST }}>{attendanceRate}%</div>
                  </div>
                  <div className="flex-1 space-y-2">
                    <div className="rounded-lg border p-2.5">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                        <span className="text-[10px] uppercase text-muted-foreground">Present</span>
                      </div>
                      <div className="font-mono text-lg font-bold">{fmtNum(presentCount)}</div>
                    </div>
                    <div className="rounded-lg border p-2.5">
                      <div className="flex items-center gap-2">
                        <XCircle className="h-4 w-4 text-red-500" />
                        <span className="text-[10px] uppercase text-muted-foreground">Absent</span>
                      </div>
                      <div className="font-mono text-lg font-bold text-red-600">{fmtNum(absentCount)}</div>
                    </div>
                    <div className="rounded-lg border p-2.5">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-amber-500" />
                        <span className="text-[10px] uppercase text-muted-foreground">Late</span>
                      </div>
                      <div className="font-mono text-lg font-bold text-amber-600">{fmtNum(lateCount)}</div>
                    </div>
                  </div>
                </div>
              </Card>

              <Card className="p-5">
                <div className="mb-4">
                  <div className="text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground">Financials</div>
                  <h3 className="mt-0.5 text-base font-semibold">Feeding Cost Breakdown</h3>
                </div>
                <div className="space-y-3">
                  <div className="rounded-lg border p-3">
                    <div className="text-[10px] uppercase text-muted-foreground">Daily Feeding Expenditure</div>
                    <div className="font-mono text-xl font-bold" style={{ color: FOREST }}>{fmtUSD(dailyCost)}</div>
                  </div>
                  <div className="rounded-lg border p-3">
                    <div className="text-[10px] uppercase text-muted-foreground">Average Cost per Meal</div>
                    <div className="font-mono text-lg font-bold" style={{ color: GOLD }}>{fmtUSD(avgCostPerMeal)}</div>
                  </div>
                  <div className="rounded-lg border p-3">
                    <div className="text-[10px] uppercase text-muted-foreground">Cost per Student</div>
                    <div className="font-mono text-lg font-bold" style={{ color: LEAF }}>{fmtUSD(costPerStudent)}</div>
                  </div>
                  <div className="rounded-lg border p-3">
                    <div className="text-[10px] uppercase text-muted-foreground">Meals Remaining</div>
                    <div className="font-mono text-lg font-bold" style={{ color: INFO }}>{fmtNum(totalRemaining)}</div>
                  </div>
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* SCHOOLS */}
          <TabsContent value="schools" className="space-y-4">
            <Card className="p-5">
              <div className="mb-4">
                <div className="text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground">Per School</div>
                <h3 className="mt-0.5 text-base font-semibold">School Meal Requirements</h3>
              </div>
              <div className="h-72">
                <ResponsiveContainer>
                  <BarChart data={schoolMealData}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                    <XAxis dataKey="name" tick={{ fontSize: 9 }} angle={-30} textAnchor="end" height={60} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <RTooltip />
                    <Bar dataKey="Planned" fill={FOREST} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Consumed" fill={GOLD} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>

            {/* School List */}
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {schools.map(s => {
                const plan = mealPlans.find(p => p.school_id === s.id);
                const portions = plan?.planned_portions ?? 0;
                const consumed = plan?.consumed_portions ?? 0;
                const status = plan?.status ?? "pending";
                return (
                  <Card key={s.id} className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="text-xs font-semibold">{s.name}</div>
                        <div className="text-[10px] text-muted-foreground">{s.district}</div>
                      </div>
                      <StatusBadge status={status} />
                    </div>
                    <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                      <div>
                        <div className="text-[9px] uppercase text-muted-foreground">Students</div>
                        <div className="font-mono text-sm font-bold">{fmtNum(s.student_count ?? 0)}</div>
                      </div>
                      <div>
                        <div className="text-[9px] uppercase text-muted-foreground">Meals</div>
                        <div className="font-mono text-sm font-bold" style={{ color: FOREST }}>{fmtNum(portions)}</div>
                      </div>
                      <div>
                        <div className="text-[9px] uppercase text-muted-foreground">Consumed</div>
                        <div className="font-mono text-sm font-bold" style={{ color: GOLD }}>{fmtNum(consumed)}</div>
                      </div>
                    </div>
                    {portions > 0 && (
                      <Progress value={portions > 0 ? (consumed / portions) * 100 : 0} className="mt-3 h-1.5" />
                    )}
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* KITCHENS */}
          <TabsContent value="kitchens" className="space-y-4">
            <Card className="p-5">
              <div className="mb-4">
                <div className="text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground">Production</div>
                <h3 className="mt-0.5 text-base font-semibold">Kitchen Load vs Capacity</h3>
              </div>
              <div className="h-64">
                <ResponsiveContainer>
                  <BarChart data={kitchenLoadData}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <RTooltip />
                    <Bar dataKey="Capacity" fill={`${SLATE}40`} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Load" fill={FOREST} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {kitchens.map(k => (
                <Card key={k.id} className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-xs font-semibold">{k.name}</div>
                      <div className="text-[10px] text-muted-foreground">{k.location}</div>
                    </div>
                    <StatusBadge status={k.status} />
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                    <div>
                      <div className="text-[9px] uppercase text-muted-foreground">Capacity</div>
                      <div className="font-mono text-sm font-bold">{fmtNum(k.capacity_per_day)}</div>
                    </div>
                    <div>
                      <div className="text-[9px] uppercase text-muted-foreground">Current</div>
                      <div className="font-mono text-sm font-bold" style={{ color: FOREST }}>{fmtNum(k.current_load)}</div>
                    </div>
                    <div>
                      <div className="text-[9px] uppercase text-muted-foreground">Utilization</div>
                      <div className="font-mono text-sm font-bold" style={{ color: GOLD }}>
                        {k.capacity_per_day > 0 ? Math.round((k.current_load / k.capacity_per_day) * 100) : 0}%
                      </div>
                    </div>
                  </div>
                  <Progress value={k.capacity_per_day > 0 ? (k.current_load / k.capacity_per_day) * 100 : 0} className="mt-3 h-1.5" />
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* INVENTORY */}
          <TabsContent value="inventory" className="space-y-4">
            <Card className="p-5">
              <div className="mb-4 flex items-start justify-between">
                <div>
                  <div className="text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground">Supply Chain</div>
                  <h3 className="mt-0.5 text-base font-semibold">Ingredient Stock Levels</h3>
                </div>
                {lowStockIngredients.length > 0 && (
                  <Badge variant="outline" className="text-[10px] text-red-600 border-red-200">
                    <AlertTriangle className="mr-1 h-3 w-3" />{lowStockIngredients.length} low
                  </Badge>
                )}
              </div>
              <div className="h-72">
                <ResponsiveContainer>
                  <BarChart data={stockData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                    <XAxis type="number" tick={{ fontSize: 10 }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={100} />
                    <RTooltip />
                    <Bar dataKey="Stock" radius={[0, 4, 4, 0]}>
                      {stockData.map((s, i) => (
                        <Cell key={i} fill={s.Stock <= s.Reorder ? ALERT : s.Stock <= s.Reorder * 2 ? GOLD : LEAF} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
            <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
              {ingredients.slice(0, 12).map(i => {
                const isLow = i.current_stock <= i.reorder_level;
                return (
                  <div key={i.id} className="rounded-lg border p-3" style={{ borderColor: isLow ? `${ALERT}40` : undefined }}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium">{i.name}</span>
                      {isLow && <Badge className="text-[9px] bg-red-50 text-red-600">Low</Badge>}
                    </div>
                    <div className="mt-1.5 flex items-baseline gap-2">
                      <span className="font-mono text-sm font-bold">{fmtNum(i.current_stock)}</span>
                      <span className="text-[10px] text-muted-foreground">{i.unit}</span>
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      Reorder at {fmtNum(i.reorder_level)} · {fmtUSD(i.cost_per_unit)}/{i.unit}
                    </div>
                    <div className="text-[9px] text-muted-foreground/60">{i.storage_location}</div>
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* DELIVERIES */}
          <TabsContent value="deliveries" className="space-y-4">
            <div className="grid gap-4 lg:grid-cols-[1fr_1fr]">
              <Card className="p-5">
                <div className="mb-4">
                  <div className="text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground">Logistics</div>
                  <h3 className="mt-0.5 text-base font-semibold">Delivery Status Distribution</h3>
                </div>
                {deliveryStatusData.length > 0 ? (
                  <div className="h-56">
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie dataKey="value" data={deliveryStatusData} cx="50%" cy="50%" outerRadius={70} label>
                          {deliveryStatusData.map((_, i) => (
                            <Cell key={i} fill={[FOREST, GOLD, INFO, ALERT, SLATE][i % 5]} />
                          ))}
                        </Pie>
                        <RTooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="py-12 text-center text-sm text-muted-foreground">No deliveries recorded</div>
                )}
              </Card>
              <Card className="p-5">
                <div className="mb-4">
                  <div className="text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground">Dispatch</div>
                  <h3 className="mt-0.5 text-base font-semibold">Delivery Manifest</h3>
                </div>
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {deliveries.map(d => (
                    <div key={d.id} className="rounded-lg border p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium">{d.school?.name ?? "—"}</span>
                        <StatusBadge status={d.status} />
                      </div>
                      <div className="mt-1.5 flex items-center gap-3 text-[10px] text-muted-foreground">
                        <span className="flex items-center gap-1"><Truck className="h-3 w-3" />{fmtNum(d.portions)} portions</span>
                        {d.dispatched_at && <span>Dispatched: {shortDate(d.dispatched_at)}</span>}
                        {d.delivered_at && <span>Delivered: {shortDate(d.delivered_at)}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* TRANSPORT */}
          <TabsContent value="transport" className="space-y-4">
            <Card className="p-5">
              <div className="mb-4">
                <div className="text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground">Fleet</div>
                <h3 className="mt-0.5 text-base font-semibold">Vehicle Fleet Status</h3>
              </div>
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
                {vehicles.map(v => (
                  <div key={v.id} className="rounded-lg border p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-mono text-sm font-bold">{v.registration}</div>
                        <div className="text-[10px] text-muted-foreground capitalize">{v.vehicle_type}</div>
                      </div>
                      <StatusBadge status={v.status} />
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-1 text-center">
                      <div>
                        <div className="text-[9px] uppercase text-muted-foreground">Capacity</div>
                        <div className="font-mono text-xs font-bold">{v.capacity}</div>
                      </div>
                      <div>
                        <div className="text-[9px] uppercase text-muted-foreground">Type</div>
                        <div className="font-mono text-xs font-bold">{v.fuel_type}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
